/*
 *     Copyright (c) 2018 Bcame S.r.l. All rights reserved.
 *     Bcame S.r.l. Technology Business Unit
 *
 *     Company confidential.
 *
 *     This software is proprietary  to and embodies the confidential
 *     technology of Bcame S.r.l.  Possession, use, or copying of this
 *     software and media is authorized only pursuant to a valid written
 *     license from Bcame S.r.l.  or an authorized sublicensor.
 *
 *     Bcame S.r.l.
 */
import Vue from 'vue'
import MetaHelper from '@/helpers/Meta'
import Config from '@/config'
import { mapState } from 'vuex'
import { getLocalizedByUrl } from '@/helpers/routeHelpers'
import isUndefined from 'lodash/isUndefined'
import assign from 'lodash/assign'
import isEmpty from 'lodash/isEmpty'

export function getCurrentCategory (store, route) {
  let searchCat = null
  if (store.state.category.categoryId) {
    searchCat = store.state.category.flatted.find(f => f.origId === store.state.category.categoryId)
  }
  if (searchCat) {
    store.commit('category/setCurrent', searchCat)
    return searchCat.origId
  } else {
    searchCat = store.state.category.flatted.find(f => f.id === store.state.category.categoryId)
    if (searchCat) {
      store.commit('category/setCurrent', searchCat)
      return searchCat.id
    }
  }
  return null
}

export function filterProducts (store, route, categoryId) {
  store.commit('ui/showLoader')
  return store.dispatch('listing/filterProducts', {
    categoryId: categoryId,
    q: route.meta.search ? route.query.q : undefined,
    router: route
  })
    .then(() => {
      store.commit('ui/hideLoader')
    })
    .catch(() => {
      store.commit('ui/hideLoader')
    })
}

/**
 * sync sort
 */
function syncSort (store, route) {
  if (!isUndefined(route.query.sort)) {
    store.commit('listing/setSort', route.query.sort)
  } else {
    store.commit('listing/setSort', Config.Theme.sort)
  }
  if (!isUndefined(route.query.dir)) {
    store.commit('listing/setSortOrientation', route.query.dir)
  } else {
    store.commit('listing/setSortOrientation', Config.Theme.sortOrientation)
  }
}

/**
 * sync start and size page with url
 */
function syncPagination (store, route) {
  let pagination = {
    start: 0,
    size: Config.Theme.paginationSize,
    page: 1
  }
  if (!isUndefined(route.query.start)) {
    pagination.start = parseInt(route.query.start)
  }
  if (!isUndefined(route.query.sz)) {
    pagination.size = parseInt(route.query.sz)
  }
  if (!isUndefined(route.query.page)) {
    pagination.page = parseInt(route.query.page)
  }
  if (!isEmpty(pagination)) {
    store.commit('listing/setPagination', pagination)
  }
}

/**
 * sync grid system
 */
function syncGridSystem (store, route) {
  if (!isUndefined(route.query.grid)) {
    store.commit('listing/setColumns', parseInt(route.query.grid))
  } else {
    store.commit('listing/setColumns', Config.Theme.gridCol)
  }
}

/**
 * sync filters from url
 */
function syncFilters (store, route) {
  if (!isUndefined(route.query.filters)) {
    try {
      const queryFilter = JSON.parse(route.query.filters)
      store.commit('listing/setFiltersChosen', queryFilter)
    } catch (err) {
      console.error('error parse filter from URL', err)
    }
  } else {
    store.commit('listing/setFiltersChosen', {})
  }
}

export default {
  getCurrentCategory,
  filterProducts,
  asyncData ({ store, route, redirect }) {
    const searchMode = route.meta.search
    if (searchMode) {
      store.commit('category/setCategoryId', null)
    }
    let categoryId = getCurrentCategory(store, route)
    syncPagination(store, route)
    syncGridSystem(store, route)
    syncSort(store, route)
    syncFilters(store, route)
    return filterProducts(store, route, categoryId)
  },
  head () {
    const metaHelper = new MetaHelper(this.$store)
    const currentMeta = {}
    if (this.category && this.category.custom_attributes) {
      this.category.custom_attributes.forEach(attr => {
        if (['meta_title', 'meta_description', 'meta_keywords', 'image'].includes(attr.attribute_code)) {
          currentMeta[attr.attribute_code] = attr.value
        }
      })
    }
    return {
      title: metaHelper.getTitle(currentMeta['meta_title'] || this.category.name),
      meta: [{
        vmid: 'description',
        name: 'description',
        content: metaHelper.getDescription(currentMeta['meta_description'])
      },
      {
        vmid: 'keywords',
        name: 'keywords',
        content: metaHelper.getKeyword(currentMeta['meta_keywords'])
      },
      {
        vmid: 'og_site_name',
        name: 'og:site_name',
        content: Config.Theme.name
      },
      {
        vmid: 'og_title',
        name: 'og:title',
        content: this.category.name
      },
      {
        vmid: 'og_description',
        name: 'og:description',
        content: metaHelper.getDescription(currentMeta['meta_description'])
      },
      {
        vmid: 'og_url',
        name: 'og:url',
        content: this.currentUrl
      },
      {
        vmid: 'og_image',
        name: 'og:image',
        content: currentMeta['image'] ? process.env.VUE_APP_MAGENTO + currentMeta['image'] : ''
      }]
    }
  },
  computed: {
    ...mapState({
      products: state => state.listing.list,
      filters: state => state.listing.filters,
      pagination: state => state.listing.pagination,
      columns: state => state.listing.columns,
      sort: state => state.listing.sort,
      category: state => state.category.current
    }),
    categoryTitle () {
      return this.category.name
    },
    subCategoryVisible () {
      if (this.category.childrenData && this.category.childrenData.length) {
        return this.category.childrenData.filter(c => c.include_in_menu && c.is_active)
      }
      return []
    },
    pathBreadcrumb () {
      let paths = [{
        text: this.$t('home'),
        to: Vue.filter('localizedByNameCategories')('home', null, null, this.$store, this.$router),
        exact: true
      }]
      if (this.category.name) {
        this.$store.getters['category/breadcrumbs'].forEach(bread => {
          paths.push({
            text: bread.category_name,
            to: getLocalizedByUrl(this.$store.state.ui.urlCode, bread.category_url_path + this.$store.state.storeConfig.config.category_url_suffix),
            active: false
          })
        })
        paths.push({
          text: this.category.name,
          active: true
        })
      }
      if (this.$route.meta.search) {
        paths.push({
          text: this.$t('Results for: {q}', this.$route.query),
          active: true
        })
      }
      return paths
    },
    displayMode () {
      if (this.category) {
        const displayMode = this.category.custom_attributes.find(customA => customA.attribute_code === 'display_mode')
        return displayMode ? displayMode.value : ''
      }
      return ''
    },
    showProducts () {
      return this.$store.getters['category/showProducts']
    },
    showBlock () {
      return this.$store.getters['category/showBlock']
    },
    currentUrl () {
      return process.env.VUE_APP_BASE_PATH + this.$route.path
    }
  },
  methods: {
    changeQuery (query) {
      history.pushState({}, null, this.$route.path + '?' +
      Object.keys(query)
        .map(key => {
          return (
            encodeURIComponent(key) + '=' + encodeURIComponent(query[key])
          )
        })
        .join('&')
      )
      // this.$router.replace({ query })
      const store = this.$store
      const route = this.$route
      let categoryId = getCurrentCategory(store, route)
      // syncPagination(store, route, false)
      syncGridSystem(store, route)
      syncSort(store, route)
      syncFilters(store, route)
      return filterProducts(store, route, categoryId)
    },
    changePage (newOffset) {
      const newQuery = assign({}, this.$route.query, { size: this.pagination.size, start: newOffset })
      this.changeQuery(newQuery)
    },
    gridChange (newGrid) {
      this.$route.query.grid = newGrid
      this.$store.commit('listing/setColumns', newGrid)
    },
    sortChange (newSort) {
      this.$route.query.sort = newSort
      if (!newSort) {
        delete this.$route.query.sort
        delete this.$route.query.dir
      }
      this.changeQuery(this.$route.query)
    },
    changeFilter (updateFilters) {
      let pagination = {
        size: Config.Theme.paginationSize,
        start: 0,
        page: 1
      }
      const _this = this
      this.$nextTick(() => {
        let querystringObj = {}
        const chosen = _this.$store.state.listing.filters.chosen
        for (let key in chosen) {
          if (chosen[key] && chosen[key].length) {
            querystringObj[key] = chosen[key]
          }
        }
        // let newQuery = assign({}, _this.$route.query, queryString)
        if (Object.keys(querystringObj).length) {
          let filters = JSON.stringify(querystringObj)
          this.$route.query.filters = filters
        } else {
          delete this.$route.query.filters
        }

        this.$route.query.size = pagination.size
        this.$route.query.start = pagination.start
        this.$route.query.page = pagination.page
        _this.$store.commit('listing/setPagination', pagination)
        _this.changeQuery(this.$route.query)
      })
    }
  }
}
